import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, InputNumber, message, Select, Space, Table } from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import salesRmQuarterTargetService from '../../services/salesRmQuarterTargetService';
import { formatFirstName } from '../../helpers/name.helpers';

const QUARTER_OPTIONS = [
  { value: 1, label: 'Q1 (Apr–Jun)' },
  { value: 2, label: 'Q2 (Jul–Sep)' },
  { value: 3, label: 'Q3 (Oct–Dec)' },
  { value: 4, label: 'Q4 (Jan–Mar)' },
];

function getCurrentFyStartYear(d = dayjs()) {
  return d.month() >= 3 ? d.year() : d.year() - 1;
}

function getCurrentQuarter(d = dayjs()) {
  const m = d.month() + 1;
  if (m >= 4 && m <= 6) return 1;
  if (m >= 7 && m <= 9) return 2;
  if (m >= 10 && m <= 12) return 3;
  return 4;
}

function buildFyOptions(centerYear) {
  return [centerYear - 1, centerYear, centerYear + 1].map((y) => ({
    value: y,
    label: `FY ${y}-${String(y + 1).slice(-2)}`,
  }));
}

function buildRowKey(member, fyStartYear, quarter, existing) {
  const target = existing?.find((t) => t.user_uuid === member.uuid);
  const revenueTarget = target?.revenue_target ?? 0;
  return {
    key: member.uuid,
    user_uuid: member.uuid,
    rm_name: member.name,
    fy_start_year: fyStartYear,
    quarter,
    revenue_target: revenueTarget,
    monthly_target: Math.round((Number(revenueTarget) / 3) * 100) / 100,
  };
}

export default function SalesQuarterTargetsPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(PERMISSIONS.SALES_RM_QUARTER_TARGETS_MANAGE);

  const [fyStartYear, setFyStartYear] = useState(() => getCurrentFyStartYear());
  const [quarter, setQuarter] = useState(() => getCurrentQuarter());
  const [rootUser, setRootUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fyOptions = useMemo(() => buildFyOptions(getCurrentFyStartYear()), []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [teamData, targets] = await Promise.all([
        salesRmQuarterTargetService.listTeamMembers(),
        salesRmQuarterTargetService.listTargets({ fyStartYear, quarter }),
      ]);
      setRootUser(teamData?.root_user ?? null);
      const members = teamData?.members ?? [];
      setRows(members.map((m) => buildRowKey(m, fyStartYear, quarter, targets)));
    } catch (e) {
      message.error(e?.message || 'Failed to load quarter targets');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fyStartYear, quarter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => String(row.rm_name || '').toLowerCase().includes(q));
  }, [rows, searchText]);

  const updateRow = (key, value) => {
    const revenueTarget = value ?? 0;
    setRows((prev) => prev.map((row) => (
      row.key === key
        ? {
          ...row,
          revenue_target: revenueTarget,
          monthly_target: Math.round((Number(revenueTarget) / 3) * 100) / 100,
        }
        : row
    )));
  };

  const handleSave = async () => {
    if (!rows.length) return;
    setSaving(true);
    try {
      const res = await salesRmQuarterTargetService.upsertTargets(
        rows.map((row) => ({
          user_uuid: row.user_uuid,
          fy_start_year: row.fy_start_year,
          quarter: row.quarter,
          revenue_target: row.revenue_target,
        })),
      );
      if (res?.success === false) {
        message.error(res?.message || 'Save failed');
        return;
      }
      message.success('Quarter targets saved');
      loadData();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'RM Name', dataIndex: 'rm_name', key: 'rm_name', fixed: 'left', width: 200, render: formatFirstName },
    {
      title: 'Quarter Revenue Target (₹)',
      dataIndex: 'revenue_target',
      key: 'revenue_target',
      width: 240,
      render: (value, record) => (
        canManage ? (
          <InputNumber
            min={0}
            value={value}
            style={{ width: '100%' }}
            onChange={(v) => updateRow(record.key, v)}
          />
        ) : value
      ),
    },
    {
      title: 'Monthly Target (₹)',
      dataIndex: 'monthly_target',
      key: 'monthly_target',
      width: 180,
      render: (value) => (
        <span className="text-gray-600">
          {new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(value) || 0)}
        </span>
      ),
    },
  ];

  const subtitle = rootUser
    ? `Combined Unlisted + Non-Unlisted revenue targets for SALES team under ${rootUser.name}. Monthly = Quarter ÷ 3.`
    : 'Combined Unlisted + Non-Unlisted revenue targets. Monthly = Quarter ÷ 3.';

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quarter Targets</h1>
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>
        <Space wrap className="sm:justify-end">
          <Select
            value={fyStartYear}
            options={fyOptions}
            onChange={setFyStartYear}
            style={{ width: 140 }}
          />
          <Select
            value={quarter}
            options={QUARTER_OPTIONS}
            onChange={setQuarter}
            style={{ width: 160 }}
          />
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Refresh
          </Button>
          {canManage && (
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
              Save
            </Button>
          )}
        </Space>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-50/50">
          <Input.Search
            placeholder="Search by RM name..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full sm:max-w-xs"
          />
          {searchText.trim() && (
            <span className="text-sm text-gray-500">
              Showing {filteredRows.length} of {rows.length}
            </span>
          )}
        </div>
        <Table
          rowKey="key"
          columns={columns}
          dataSource={filteredRows}
          loading={loading}
          pagination={false}
          scroll={{ x: 640 }}
          size="middle"
        />
      </div>
    </DashboardPageWrapper>
  );
}
